import React, { useEffect, useState, useRef, Fragment } from 'react';
import { PhoneIcon, MailIcon, ShieldCheckIcon, CheckIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import { ScreenHeader } from './ScreenHeader';
import type { TransferSummary } from './TransferFormScreen';
export function TransferOtpScreen({
  summary,
  onBack,
  onComplete
}: {
  summary: TransferSummary;
  onBack: () => void;
  onComplete: () => void;
}) {
  const [method, setMethod] = useState<'mobile' | 'email'>('mobile');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [seconds, setSeconds] = useState(60);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);
  useEffect(() => {
    setDigits(['', '', '', '', '', '']);
    setSeconds(60);
    inputs.current[0]?.focus();
  }, [method]);
  const handleDigit = (idx: number, val: string) => {
    const clean = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = clean;
    setDigits(next);
    if (clean && idx < 5) inputs.current[idx + 1]?.focus();
  };
  const handleKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };
  const code = digits.join('');
  const canConfirm = code.length === 6;
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F4F7FA'
  }} data-id="element-779">
      <StatusBar data-id="element-780" />
      <ScreenHeader title="Verify Transfer" subtitle="Step 2 of 2 · One-time password" onBack={onBack} data-id="element-781" />

      <div className="flex-1 overflow-y-auto px-5 pb-4" data-id="element-782">
        {/* Summary card */}
        <div className="rounded-3xl bg-white p-4 mb-5" style={{
        border: '1px solid #E5EAF0',
        boxShadow: '0 1px 2px rgba(15,23,42,0.03)'
      }} data-id="element-783">
          <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#64748B',
          letterSpacing: '0.06em'
        }} data-id="element-784">
            YOU'RE SENDING
          </div>
          <div className="mt-1 tracking-tight" style={{
          fontSize: 26,
          fontWeight: 700,
          color: '#1A1A1A'
        }} data-id="element-785">
            LKR {summary.amount || '0.00'}
          </div>
          <div className="mt-3 h-px" style={{
          backgroundColor: '#F1F5F9'
        }} data-id="element-786" />
          <SummaryRow label="From" value={summary.source} data-id="element-787" />
          <SummaryRow label="To" value={summary.recipient} data-id="element-788" />
          <SummaryRow label="Account" value={summary.accountNo} data-id="element-789" />
          {summary.reference && <SummaryRow label="Reference" value={summary.reference} last data-id="element-790" />}
        </div>

        {/* OTP method toggle */}
        <div className="mb-3" style={{
        fontSize: 12.5,
        fontWeight: 700,
        color: '#1A1A1A'
      }} data-id="element-791">
          Send code via
        </div>
        <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl mb-5" style={{
        backgroundColor: '#E5EAF0'
      }} data-id="element-792">
          <MethodTab active={method === 'mobile'} icon={<PhoneIcon size={16} data-id="element-794" />} label="Mobile" sub="•••• 4521" onClick={() => setMethod('mobile')} data-id="element-793" />
          <MethodTab active={method === 'email'} icon={<MailIcon size={16} data-id="element-796" />} label="Email" sub="k•••@email.com" onClick={() => setMethod('email')} data-id="element-795" />
        </div>

        {/* OTP input */}
        <div className="mb-2" style={{
        fontSize: 13,
        color: '#525252',
        lineHeight: 1.55
      }} data-id="element-797">
          We sent a 6-digit code to your{' '}
          <span style={{
          fontWeight: 700,
          color: '#1A1A1A'
        }} data-id="element-798">
            {method === 'mobile' ? 'mobile' : 'email'}
          </span>
          . Enter it below to confirm.
        </div>
        <div className="flex items-center justify-center gap-2 mb-3" data-id="element-799">
          {digits.map((d, i) => {
          const isFocused = focusedIdx === i;
          const isNext = focusedIdx === null && code.length === i;
          const highlight = isFocused || isNext;
          const cell = <input key={i} ref={el => inputs.current[i] = el} inputMode="numeric" maxLength={1} value={d} onChange={e => handleDigit(i, e.target.value)} onKeyDown={e => handleKey(i, e)} onFocus={() => setFocusedIdx(i)} onBlur={() => setFocusedIdx(null)} aria-label={`Digit ${i + 1}`} className="text-center rounded-xl bg-white outline-none transition-all" style={{
            width: 44,
            height: 56,
            fontSize: 22,
            fontWeight: 700,
            color: '#1A1A1A',
            border: d ? '1.5px solid #009AC7' : highlight ? '1.5px solid #009AC7' : '1.5px solid #E5EAF0',
            boxShadow: highlight ? '0 0 0 3px rgba(0,154,199,0.15)' : d ? '0 0 0 3px rgba(0,154,199,0.08)' : 'none',
            caretColor: '#009AC7'
          }} data-id="element-800" />;
          return <Fragment key={i} data-id="element-801">
                {cell}
                {i === 2 && <span aria-hidden="true" className="shrink-0 rounded-full" style={{
              width: 8,
              height: 2,
              backgroundColor: '#CBD5E1'
            }} data-id="element-802" />}
              </Fragment>;
        })}
        </div>

        <div className="flex items-center justify-between" data-id="element-803">
          <span style={{
          fontSize: 12,
          color: '#64748B'
        }} data-id="element-804">
            {seconds > 0 ? <>
                Resend in{' '}
                <span style={{
              fontWeight: 700,
              color: '#1A1A1A'
            }} data-id="element-805">
                  {seconds}s
                </span>
              </> : 'Didn’t get the code?'}
          </span>
          <button type="button" disabled={seconds > 0} onClick={() => setSeconds(60)} className="transition-colors disabled:opacity-40" style={{
          fontSize: 13,
          fontWeight: 700,
          color: '#009AC7',
          padding: '8px 4px'
        }} data-id="element-806">
            Resend code
          </button>
        </div>
      </div>

      {/* Sticky CTA */}
      <div className="px-5 pt-3 pb-3" style={{
      backgroundColor: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid #E5EAF0'
    }} data-id="element-807">
        <button type="button" disabled={!canConfirm} onClick={() => canConfirm && onComplete()} className="w-full rounded-2xl text-white font-semibold transition-all active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed" style={{
        backgroundColor: '#009AC7',
        minHeight: 56,
        fontSize: 16,
        boxShadow: canConfirm ? '0 12px 28px -10px rgba(0,154,199,0.55)' : 'none',
        letterSpacing: '0.01em'
      }} data-id="element-808">
          Confirm & Transfer
        </button>
        <div className="flex items-center justify-center gap-1.5 mt-2.5" data-id="element-809">
          <ShieldCheckIcon size={12} style={{
          color: '#009AC7'
        }} data-id="element-810" />
          <span style={{
          fontSize: 11,
          color: '#525252',
          fontWeight: 500
        }} data-id="element-811">
            End-to-end encrypted · CBSL secured
          </span>
        </div>
      </div>
      <div className="flex justify-center pb-2 bg-white" data-id="element-812">
        <div className="w-[120px] h-[5px] rounded-full bg-neutral-900/80" aria-hidden="true" data-id="element-813" />
      </div>
    </div>;
}
function MethodTab({
  active,
  icon,
  label,
  sub,
  onClick
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 rounded-xl transition-all" style={{
    minHeight: 48,
    backgroundColor: active ? '#FFFFFF' : 'transparent',
    boxShadow: active ? '0 2px 6px rgba(15,23,42,0.08)' : 'none',
    color: active ? '#1A1A1A' : '#64748B'
  }} data-id="element-814">
      <span style={{
      color: active ? '#009AC7' : '#94A3B8'
    }} data-id="element-815">
        {icon}
      </span>
      <div className="text-left leading-tight" data-id="element-816">
        <div style={{
        fontSize: 13,
        fontWeight: 700
      }} data-id="element-817">
          {label}
        </div>
        <div style={{
        fontSize: 10.5,
        fontWeight: 500,
        opacity: 0.75
      }} data-id="element-818">
          {sub}
        </div>
      </div>
    </button>;
}
function SummaryRow({
  label,
  value,
  last
}: {
  label: string;
  value: string;
  last?: boolean;
}) {
  return <div className="flex items-center justify-between py-2.5" style={{
    borderBottom: last ? 'none' : '1px solid #F1F5F9'
  }} data-id="element-819">
      <span style={{
      fontSize: 12,
      color: '#64748B',
      fontWeight: 500
    }} data-id="element-820">
        {label}
      </span>
      <span className="text-right truncate ml-3" style={{
      fontSize: 13.5,
      color: '#1A1A1A',
      fontWeight: 600
    }} data-id="element-821">
        {value || '—'}
      </span>
    </div>;
}